var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
// Components
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
import { Asset } from '@frontend_zz/asset/Asset.js';
import { Button } from '@frontend_zz/button/Button.js';
import { Collapsible } from '@frontend_zz/collapsible/Collapsible.js';
import { Icon } from '@frontend_zz/icon/Icon.js';
import { List, ListItem } from '@frontend_zz/list/List.js';
import { Surface } from '@frontend_zz/surface/Surface.js';
import { Text } from '@frontend_zz/text/Text.js';
import { TextInput } from '@frontend_zz/components/atoms/TextInput/TextInput.js';
// Styling
import * as styles from './ServiceAndContact.css.js';
import { litVisuallyHidden } from '@frontend_zz/web-component-utilities/styles/utility/misc.css.js';
import { litLayout } from '@frontend_zz/web-component-utilities/styles/utility/layout.css.js';
// Utils
const normaliseContent = (content) => (typeof content === 'string' ? JSON.parse(content) : content);
/**
 * @element zz-service-and-contact-hero
 */
export class ServiceAndContactHero extends BaseElement {
    constructor() {
        super(...arguments);
        this.formRef = createRef();
        this.render = () => {
            const { content, formRef } = this;
            const onSubmitClick = (event) => {
                if (event.type === 'keyup' && event.key !== 'Enter')
                    return;
                formRef.value?.submit();
                this.dispatchEvent(new CustomEvent('search', {
                    detail: {
                        query: event.target.value,
                    },
                }));
            };
            return html `
            <div class="pillar-max">
                <div class="search flex flex-col gap-l">
                    <zz-text tag="h1">${content.heading}</zz-text>
                    <zz-text>${content.intro}</zz-text>
                    <form ${ref(formRef)} class="flex flex-wrap gap-s" action=${content.search.actionHref}>
                        <zz-text-input
                            class="input"
                            name=${content.search.fieldName}
                            label=${content.search.buttonLabel}
                            hide-label
                            @keyup=${onSubmitClick}
                        ></zz-text-input>
                        <zz-button
                            class="submit"
                            type="submit"
                            variant="secondary"
                            icon="searchZoom"
                            @click=${onSubmitClick}
                        >
                            ${content.search.buttonLabel}
                        </zz-button>
                    </form>
                </div>
            </div>
        `;
        };
    }
    static get styles() {
        return [styles.hero, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-button': Button,
            'zz-text': Text,
            'zz-text-input': TextInput,
        };
    }
    updated(changedProperties) {
        const { content } = this;
        if (changedProperties.has('content')) {
            this.style.setProperty('--service-and-contact-hero-bg-url', `url('${content.backgroundUrl}')`);
        }
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactHero.prototype, "content", void 0);
/**
 * @element zz-service-and-contact-popular-topics
 */
export class ServiceAndContactPopularTopics extends BaseElement {
    constructor() {
        super(...arguments);
        this.renderCard = ({ label, icon, href }) => {
            const { isMobile } = this;
            const buttonContentClasses = classMap({
                'label flex flex-align-center gap-l': true,
                'flex-col': !isMobile,
            });
            return html `
            <zz-surface variant="elevated" clickable>
                <zz-button class="width-100pct" variant="text" href=${href}>
                    <div class=${buttonContentClasses}>
                        <zz-icon type=${icon} size="xxl"></zz-icon>
                        <zz-text tag="h6">${label}</zz-text>
                    </div>
                </zz-button>
            </zz-surface>
        `;
        };
        this.render = () => {
            const { content, renderCard, isMobile } = this;
            return html `
            <div class="pillar-max flex flex-col gap-l">
                <zz-text tag="h2">${content.heading}</zz-text>
                <div class=${`grid gap-m ${isMobile ? 'grid-col' : ''}`}>${repeat(content.items, renderCard)}</div>
            </div>
        `;
        };
    }
    static get styles() {
        return [styles.popularTopics, styles.layout, litVisuallyHidden, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-button': Button,
            'zz-icon': Icon,
            'zz-surface': Surface,
            'zz-text': Text,
        };
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactPopularTopics.prototype, "content", void 0);
/**
 * @element zz-service-and-contact-links
 */
export class ServiceAndContactLinks extends BaseElement {
    constructor() {
        super(...arguments);
        this.render = () => {
            const { content } = this;
            return html `
            <div class="pillar-max flex flex-col gap-l">
                <zz-text class="visually-hidden" tag="h2">${content.heading}</zz-text>
                <div class="link-blocks">
                    <slot></slot>
                </div>
            </div>
        `;
        };
    }
    static get styles() {
        return [styles.links, litVisuallyHidden, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-text': Text,
        };
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactLinks.prototype, "content", void 0);
/**
 * @element zz-service-and-contact-links-block
 */
export class ServiceAndContactLinksBlock extends BaseElement {
    constructor() {
        super(...arguments);
        this.isOpen = false;
        this.renderLinksList = ({ label, href }) => {
            return html `
            <zz-list-item class="list-item" icon="chevronRight">
                <zz-button variant="text" inline icon-size="s" href=${href}>${label}</zz-button>
            </zz-list-item>
        `;
        };
        this.renderLinksBlockMobile = () => {
            const { content, renderLinksList, isOpen } = this;
            const onClick = (_event) => {
                this.isOpen = !isOpen;
            };
            return html `
            <div class="flex flex-col gap-l">
                <zz-button variant="text" inline @click=${onClick}>
                    <div class="flex gap-l">
                        <zz-icon class="trigger-icon" ?data-is-active=${isOpen} type="chevronDown"></zz-icon>
                        <zz-text tag="h3" style-as="h4">${content.heading}</zz-text>
                    </div>
                </zz-button>
                <zz-collapsible ?is-open=${isOpen}>
                    <zz-list class="list">
                        <div class="flex flex-col gap-s">${repeat(content.items, renderLinksList)}</div>
                    </zz-list>
                </zz-collapsible>
            </div>
        `;
        };
        this.renderLinksBlockDesktop = () => {
            const { content, renderLinksList } = this;
            return html `
            <div class="flex flex-col gap-l">
                <zz-text tag="h3" style-as="h4">${content.heading}</zz-text>
                <zz-list>
                    <div class="flex flex-col gap-s">${repeat(content.items, renderLinksList)}</div>
                </zz-list>
            </div>
        `;
        };
        this.render = () => {
            const { renderLinksBlockMobile, renderLinksBlockDesktop, isMobile } = this;
            return isMobile ? renderLinksBlockMobile() : renderLinksBlockDesktop();
        };
    }
    static get styles() {
        return [styles.linksBlock, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-button': Button,
            'zz-collapsible': Collapsible,
            'zz-icon': Icon,
            'zz-list': List,
            'zz-list-item': ListItem,
            'zz-text': Text,
        };
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactLinksBlock.prototype, "content", void 0);
__decorate([
    state()
], ServiceAndContactLinksBlock.prototype, "isOpen", void 0);
/**
 * @element zz-service-and-contact-usps
 */
export class ServiceAndContactUsps extends BaseElement {
    constructor() {
        super(...arguments);
        this.renderListItem = (usp) => {
            return html `
            <zz-list-item class="list-item" icon="check" theme="blue">
                <zz-text theme="blue">${usp}</zz-text>
            </zz-list-item>
        `;
        };
        this.render = () => {
            const { content, renderListItem, isDesktop } = this;
            return html `
            <div class=${`pillar-max flex gap-l ${!isDesktop ? 'flex-col' : ''}`}>
                <zz-text class="heading" tag="h2" style-as="h4" theme="blue">${content.heading}</zz-text>
                <zz-list class="list" theme="blue">
                    <div class="list-content flex flex-col gap-m">${repeat(content.items, renderListItem)}</div>
                </zz-list>
                <div class="flex flex-align-center gap-s">
                    <zz-button variant="secondary" icon="gebruiker" href=${content.mijnZzButtonHref} theme="blue">
                        ${content.mijnZzButtonLabel}
                    </zz-button>
                    <zz-asset class="digid-asset flex flex-align-center" type="digid"></zz-asset>
                </div>
            </div>
        `;
        };
    }
    static get styles() {
        return [styles.usps, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-asset': Asset,
            'zz-button': Button,
            'zz-list': List,
            'zz-list-item': ListItem,
            'zz-text': Text,
        };
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactUsps.prototype, "content", void 0);
/**
 * @element zz-service-and-contact-contact-options
 */
export class ServiceAndContactContactOptions extends BaseElement {
    constructor() {
        super(...arguments);
        this.renderCard = ({ id, label, icon, description, linkLabel, href }) => {
            const { isMobile } = this;
            const onClick = (_event) => {
                // Broadcast activation of an option; needed for GX so they can trigger things like a chat widget
                this.dispatchEvent(new CustomEvent('option-click', {
                    detail: {
                        id,
                    },
                }));
            };
            return html `
            <zz-surface id=${ifDefined(id)} class="card" variant="elevated-not-clickable-centered">
                <div class=${`flex gap-l ${!isMobile ? 'flex-col flex-align-center' : ''}`}>
                    <zz-icon class="flex" type=${icon} size="xxxl"></zz-icon>
                    <div class="width-100pct flex flex-col flex-grow-children gap-m">
                        <zz-text style-as="h4">${label}</zz-text>
                        <zz-text class="flex-grow">${description}</zz-text>
                        <zz-button
                            class="link"
                            variant="text"
                            href=${ifDefined(href)}
                            icon="chevronRight"
                            icon-size="xs"
                            @click=${onClick}
                        >
                            ${linkLabel}
                        </zz-button>
                    </div>
                </div>
            </zz-surface>
        `;
        };
        this.render = () => {
            const { content, renderCard, isMobile } = this;
            return html `
            <div class="pillar-max flex flex-col gap-l">
                <zz-text class="heading" tag="h2">${content.heading}</zz-text>
                <div class=${`grid gap-l ${isMobile ? 'grid-col' : ''}`}>${repeat(content.items, renderCard)}</div>
            </div>
        `;
        };
    }
    static get styles() {
        return [styles.contactOptions, styles.layout, litLayout];
    }
    static get scopedElements() {
        return {
            'zz-button': Button,
            'zz-icon': Icon,
            'zz-text': Text,
            'zz-surface': Surface,
        };
    }
}
__decorate([
    property({ type: Object, converter: normaliseContent })
], ServiceAndContactContactOptions.prototype, "content", void 0);
